import React, { ForwardedRef, forwardRef, useState } from 'react';
import {
  ClientSideRowModelModule, GridApi, GridOptions, GridReadyEvent, GridSizeChangedEvent, ModuleRegistry
} from 'ag-grid-community';
import { ClipboardModule, LicenseManager, MasterDetailModule } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { licenceKey } from '@access-erp/common';

import styles from './styles/table-grid.module.scss';

LicenseManager.setLicenseKey(licenceKey);

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  MasterDetailModule,
  ClipboardModule,
]);

interface ITableGridProps<TData = any> extends GridOptions<TData> {
  columnDefs: NonNullable<GridOptions['columnDefs']>;
  className?: string;
}

function TableGridInner<TData = any>(props: ITableGridProps<TData>, ref?: ForwardedRef<AgGridReact>) {
  const {
    onGridReady,
    popupParent,
    domLayout = 'autoHeight',
    suppressMenuHide = true,
    suppressContextMenu = true,
    suppressRowGroupHidesColumns = true,
    suppressDragLeaveHidesColumns = true,
    headerHeight = 37,
    defaultColDef,
    ...restProps
  } = props;
  const [, setGridApi] = useState<GridApi>();

  const onGridReadyInitial = (params: GridReadyEvent) => {
    setGridApi(params.api);
    onGridReady?.(params);
  };

  const onGridSizeChanged = ({ api, clientWidth }: GridSizeChangedEvent) => {
    if (!api) {
      return;
    }

    try {
      const columnState = api.getColumnState();
      if (!columnState || !Array.isArray(columnState)) {
        return;
      }

      const sumColumnsWidth = columnState
        .reduce((accum, value) => accum + (value?.width ?? 0), 0);

      // clientWidth might be bigger than sumColumnsWidth because of borders
      // so need to consider also difference
      const diff = clientWidth - sumColumnsWidth;
      const maxAllowedDiffPixels = 2;

      if (clientWidth > sumColumnsWidth && diff > maxAllowedDiffPixels) {
        api.sizeColumnsToFit();
      }
    } catch (error) {
      // Silently handle any API errors during grid size change
      console.warn('Error during grid size change:', error);
    }
  };

  return (
    <div className={styles['table-grid']}>
      <div className='ag-theme-alpine'>
        <AgGridReact<TData>
          ref={ref}
          reactiveCustomComponents={false}
          domLayout={domLayout}
          onGridReady={onGridReadyInitial}
          onGridSizeChanged={onGridSizeChanged}
          suppressContextMenu={suppressContextMenu}
          suppressMenuHide={suppressMenuHide}
          suppressRowGroupHidesColumns={suppressRowGroupHidesColumns}
          suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
          headerHeight={headerHeight}
          {...restProps}
          defaultColDef={{
            cellDataType: false,
            ...defaultColDef,
          }}
        />
      </div>
    </div>
  );
}

// To make forwardRef as generic
export const TableGrid = forwardRef(TableGridInner) as <TData>(
  props: ITableGridProps<TData> & { ref?: React.ForwardedRef<AgGridReact> }
) => ReturnType<typeof TableGridInner>;
